import React, { useState, useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import ViewIcon from "@material-ui/icons/Visibility";
import OffIcon from "@material-ui/icons/VisibilityOff";
import RefreshIcon from "@material-ui/icons/Refresh";
import ReCaptcha from "./Recaptcha";
const viz_url = process.env.REACT_APP_VIZ_URL;

const LoginForm = ({
  onSubmit,
  classes,
  errorMessage,
  open,
  setErrorMessage,
  persistentAlert,
  handleChangeLocation,
  onSubmitWithRecaptcha,
}) => {
  const [token, setToken] = useState('');
  const [submitCount, setSubmitCount] = useState(0);
  const [recaptchaKey, setRecaptchaKey] = useState(0); // Key to force re-render of reCAPTCHA
  const recaptchaRef = useRef(null);

  const handleToken = (token) => {
    setToken(token);
  };

  const handleRefreshRecaptcha = () => {
    // Reset the token
    setToken('');
    // Force re-render of reCAPTCHA component by changing key
    setRecaptchaKey(prev => prev + 1);
    // If you have access to reCAPTCHA reset method, you can also call it
    if (recaptchaRef.current && recaptchaRef.current.reset) {
      recaptchaRef.current.reset();
    }
  };

  const handleFormSubmit = (values) => {
    const newCount = submitCount + 1;
    setSubmitCount(newCount);

    if (newCount > 5) {
      if (token) {
        onSubmitWithRecaptcha({ ...values, token });
        // Reset reCAPTCHA after submission for potential retry
        setTimeout(() => {
          handleRefreshRecaptcha();
        }, 2000);
      } else {
        // Don't submit if no token available
        return;
      }
    } else {
      onSubmit({ ...values });
    }
  };

  let disablingCondition = false;
  if (submitCount > 5 && token === '') {
    disablingCondition = true;
  }

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
        checked: false,
        visible: false,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email("Must be a valid email.")
          .max(255)
          .required("Email is required."),
        password: Yup.string().max(255).required("Password is required."),
      })}
      onSubmit={handleFormSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        setFieldValue,
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formStyle}
          style={{ paddingTop: "10%" }}
        >
          {errorMessage && (
            <Collapse in={open}>
              <Alert
                severity="error"
                variant="filled"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setErrorMessage("")}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <Typography variant="body1">{errorMessage}</Typography>
              </Alert>
            </Collapse>
          )}
          <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            label="Email Address"
            margin="normal"
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            label="Password"
            margin="normal"
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.password}
            variant="outlined"
            type={values.visible ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setFieldValue("visible", !values.visible, false)}
                >
                  {values.visible ? <OffIcon /> : <ViewIcon />}
                </IconButton>
              ),
            }}
          />
          <Box mt={2}>
            <Button
              color="primary"
              disabled={disablingCondition}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Sign in
            </Button>
          </Box>
          <Box mt={2}>
            <Checkbox
              checked={values.checked}
              color="primary"
              className={classes.rememberMeCheck}
              onChange={handleChange}
              name="checked"
            />
            Remember me
          </Box>
          {submitCount > 5 && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {/* <IconButton
                  onClick={handleRefreshRecaptcha}
                  color="primary"
                  title="Refresh reCAPTCHA"
                  size="small"
                >
                  <RefreshIcon />
                </IconButton> */}
                <Box flex={1}>
                  <ReCaptcha 
                    key={recaptchaKey} // Force re-render when key changes
                    ref={recaptchaRef}
                    siteKey={"6LeEEwYrAAAAAMRPP6WIE60QON6JPXKpJH8rRZBA"} 
                    callback={handleToken} 
                  />
                </Box>
                
              </Box>
              {/* <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                Click the refresh button to get a new reCAPTCHA if needed
              </Typography> */}
            </Box>
          )}
          <Grid container spacing={2} style={{ marginTop: 10 }}>
            <Grid item xs={12} mt={2} className={classes.backButtonContainer}>
              <Button
                disabled={
                  persistentAlert !== "" &&
                  persistentAlert.message === "Signing in..."
                }
                size="small"
                variant="small"
                onClick={() => handleChangeLocation("forgotPass")}
                className={classes.backButton}
              >
                Forgot Your Password?
              </Button>
            </Grid>
            <Grid item xs={12} mt={2} className={classes.backButtonContainer}>
              <Button
                size="small"
                variant="small"
                href={`${viz_url}/api/auth/salesforce`}
                className={classes.backButton}
              >
                Sign in with Salesforce
              </Button>
            </Grid>
            <Grid item xs={12} mt={2} className={classes.backButtonContainer}>
              <Button
                size="small"
                variant="small"
                href={`${viz_url}/api/auth/activeDirectory`}
                className={classes.backButton}
              >
                Sign in with Microsoft
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default LoginForm;